import React, { useState } from 'react';
import {
  downloadICS,
  getGoogleCalendarUrl,
  getOutlookCalendarUrl,
  getYahooCalendarUrl,
  isAppleDevice,
  isAndroid
} from '../utils/calendarUtils';

const AddToCalendar = ({ appointment, compact = false }) => {
  const [showMenu, setShowMenu] = useState(false);

  const handleAddToCalendar = (type) => {
    switch (type) {
      case 'google':
        window.open(getGoogleCalendarUrl(appointment), '_blank');
        break;
      case 'outlook':
        window.open(getOutlookCalendarUrl(appointment), '_blank');
        break;
      case 'yahoo':
        window.open(getYahooCalendarUrl(appointment), '_blank');
        break;
      case 'ical':
      case 'apple':
        downloadICS(appointment);
        break;
      default:
        downloadICS(appointment);
    }
    setShowMenu(false);
  };

  const getDefaultOptions = () => {
    const options = [];
    
    // Google Calendar (universal)
    options.push({
      type: 'google',
      label: 'Google Calendar',
      icon: '📅',
      color: 'text-red-600'
    });
    
    // Apple Calendar for iOS/Mac
    if (isAppleDevice()) {
      options.push({
        type: 'apple',
        label: 'Apple Calendar',
        icon: '🍎',
        color: 'text-gray-800'
      });
    }
    
    // Outlook
    options.push({
      type: 'outlook',
      label: 'Outlook',
      icon: '📧',
      color: 'text-blue-600'
    });
    
    // Download ICS (works with all calendar apps)
    options.push({
      type: 'ical',
      label: 'Download .ics',
      icon: '⬇️',
      color: 'text-gray-600'
    });
    
    return options;
  };

  if (compact) {
    return (
      <div className="relative w-full">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors whitespace-nowrap"
        >
          <span>📅</span>
          <span>Add to Calendar</span>
        </button>
        
        {showMenu && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowMenu(false)}
            />
            
            {/* Dropdown Menu */}
            <div className="absolute end-0 mt-2 w-56 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-xl border border-gray-200 z-50">
              <div className="py-2">
                {getDefaultOptions().map((option) => (
                  <button
                    key={option.type}
                    onClick={() => handleAddToCalendar(option.type)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <span className="text-xl">{option.icon}</span>
                    <span className={`text-sm font-medium ${option.color}`}>
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Full-size button with dropdown
  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 font-medium"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span>Add to Calendar</span>
        <svg className={`w-4 h-4 transition-transform ${showMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {showMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowMenu(false)}
          />
          
          {/* Dropdown Menu */}
          <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 px-4 py-3 border-b border-gray-200">
              <p className="text-sm font-semibold text-gray-700">Choose your calendar</p>
            </div>
            <div className="py-1">
              {getDefaultOptions().map((option) => (
                <button
                  key={option.type}
                  onClick={() => handleAddToCalendar(option.type)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <span className="text-2xl">{option.icon}</span>
                  <div className="flex-1">
                    <span className={`text-sm font-medium ${option.color} block`}>
                      {option.label}
                    </span>
                    {option.type === 'ical' && (
                      <span className="text-xs text-gray-500">
                        Works with any calendar app
                      </span>
                    )}
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddToCalendar;
